import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { isFieldEnrolled, useFeatureFlag, useGetAllFieldPracticesByFarm, useGetFarmReportingYear, useGetFarms, useTranslation } from "utils";

import { FEATURE_FLAGS } from "consts";

import { FieldListField } from "models";

import { Button, Card, ExtraInfo, Icon, LinkMoreInfo, ProgressBar, Tooltip, Typography } from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";

export type BulkFieldContextMenuProps = {
  allFields: FieldListField[];
  children?: ReactNode;
  fieldsWithOpenTasksCount: number;
  visible: boolean;
  onAddFields: () => void;
  onClose: (value: boolean) => void;
  onDeleteFields: () => void;
  onEnrollFields: () => void;
};

const getPlacementStyle = (parentDiv: HTMLDivElement | null, menuDiv: HTMLDivElement | null) => {
  const defaults = {
    position: "fixed" as React.CSSProperties["position"],
    maxHeight: "calc(100vh - 80px)",
    overflowY: "auto" as const,
    zIndex: 1001,
  };

  if (!parentDiv || !menuDiv) {
    return defaults;
  }

  const { top, right } = parentDiv.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const menuHeight = 500;
  const bottomPadding = 16;
  const headerHeight = 80;

  const wouldOverflowBottom = top + menuHeight > viewportHeight - bottomPadding;
  const adjustedTop = wouldOverflowBottom ? Math.max(headerHeight, viewportHeight - menuHeight - bottomPadding) : top;

  return {
    ...defaults,
    top: `${adjustedTop + 20}px`,
    left: `calc(${right}px + 1rem)`,
  };
};

export const BulkFieldContextMenu = ({
  allFields,
  children,
  fieldsWithOpenTasksCount,
  visible,
  onAddFields,
  onClose,
  onDeleteFields,
  onEnrollFields,
}: BulkFieldContextMenuProps) => {
  const { t } = useTranslation(["allFieldView", "onboarding"]);
  const { currentFarm } = useGetFarms();
  const { data: allFieldsPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { isDataSubmitted, isDataSubmissionReopened } = useSubmissionStatus(activeFarmReportingYear);
  const isEnrollmentLockedBySubmission = isDataSubmitted && !isDataSubmissionReopened;
  const enrollFieldsDisabledReason = isEnrollmentLockedBySubmission ? t("onboarding:fieldDetails.carbonProgram.enrollmentDisabledDataSubmitted") : undefined;

  const parentRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [placementStyle, setPlacementStyle] = useState(getPlacementStyle(parentRef.current, menuRef.current));
  const [showHectareLimitInfoDrawer, setShowHectareLimitInfoDrawer] = useState<boolean>(false);

  useEffect(() => {
    const updatePlacement = () => {
      setPlacementStyle(getPlacementStyle(parentRef.current, menuRef.current));
    };

    updatePlacement();

    window.addEventListener("resize", updatePlacement);
    window.addEventListener("scroll", updatePlacement, { passive: true });

    return () => {
      window.removeEventListener("resize", updatePlacement);
      window.removeEventListener("scroll", updatePlacement);
    };
  }, [visible]);

  const stats = useMemo(() => {
    const totalFields = allFields.length;

    const enrolledFields = allFields.filter((field) => {
      const practiceBundle = allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [];
      return isFieldEnrolled(practiceBundle, currentFarm?.carbonProgram ?? null);
    });

    const enrolledFieldArea = enrolledFields.reduce((acc, field: FieldListField) => acc + field.size, 0);
    const enrolledFieldCount = enrolledFields.length;
    // Count fields with missing/invalid data among enrolled fields
    const missingDataCount = fieldsWithOpenTasksCount;

    return { totalFields, enrolledFieldArea, enrolledFieldCount, missingDataCount };
  }, [allFields, fieldsWithOpenTasksCount, allFieldsPractices, currentFarm?.carbonProgram]);

  // TODO: May be in future need to handle it properly so this is as per Paul's suggestion
  const { isEnabled: isSoilSightEnabled } = useFeatureFlag(FEATURE_FLAGS.SOIL_SIGHT_INTEGRATION);
  const shouldShowInsightsImpactPanel = isSoilSightEnabled && !!currentFarm?.carbonProgram?.platformFeatures?.fieldManagement?.insightsImpactPanel;

  const hectareLimitAsSafeNumber = currentFarm?.hectareLimit ?? 0;

  const enrollmentPercentage = stats.totalFields > 0 ? Math.round((stats.enrolledFieldCount / stats.totalFields) * 100) : 0;
  const hectareLimitReached = stats.enrolledFieldArea >= hectareLimitAsSafeNumber;

  return (
    <div className="context-menu-container" ref={parentRef}>
      {children}
      {visible &&
        createPortal(
          <>
            <div className="context-menu-mask" onClick={() => onClose(false)} />
            <div className="bulk-field-context-menu" style={placementStyle} ref={menuRef}>
              <Card bodyStyle={{ padding: 0 }}>
                {/* Overview Section */}
                <div className="overview-section">
                  <Typography variant="h3" className="overview-title">
                    {t("fieldContextMenu.overview")}
                  </Typography>

                  <div className="overview-row">
                    <Typography variant="paragraph-m" className="overview-label-primary">
                      {t("fieldContextMenu.allFields")}
                    </Typography>
                    <Typography variant="paragraph-m" className="overview-value-primary">
                      {stats.totalFields}
                    </Typography>
                  </div>
                  {!shouldShowInsightsImpactPanel && (
                    <div className="overview-row">
                      <Typography variant="paragraph-m" className="overview-label">
                        {t("fieldContextMenu.enrolledFields")}
                      </Typography>
                      <Typography variant="paragraph-m" className="overview-value">
                        {stats.enrolledFieldCount}/{stats.totalFields}
                      </Typography>
                    </div>
                  )}
                  <div className="overview-row">
                    <Typography variant="paragraph-m" className="overview-label">
                      {t("fieldContextMenu.missingData")}
                    </Typography>
                    <Typography variant="paragraph-m" className="overview-value">
                      {stats.missingDataCount}/{stats.enrolledFieldCount}
                    </Typography>
                  </div>
                </div>

                {/* Enroll Fields Section with Blue Background */}
                {!shouldShowInsightsImpactPanel && (
                  <div className="enroll-section">
                    <div className="enroll-header">
                      <Typography variant="h4" className="enroll-title">
                        {t("fieldContextMenu.enrollFields")}
                      </Typography>
                      <Icon iconName="tabler/layout-grid-add" size="m" color="#8ab0f9" />
                    </div>

                    <Typography variant="paragraph-m" className="enroll-description">
                      {t("fieldContextMenu.enrollDescription")}
                    </Typography>

                    <div className="progress-section">
                      <ProgressBar percent={enrollmentPercentage} showInfo={false} strokeColor="#8AB0F9" trailColor="#f4f4f4" />
                      <div className="progress-label-row">
                        <Typography variant="paragraph-m" className="progress-label">
                          {t("fieldContextMenu.enrolled")}
                        </Typography>
                        <Typography variant="paragraph-m" className="progress-value">
                          {stats.enrolledFieldCount}/{stats.totalFields} {t("fieldContextMenu.fields")}
                        </Typography>
                      </div>
                      {currentFarm && (
                        <div className="progress-label-row">
                          <Typography variant="paragraph-m" className="progress-label">
                            {t("fieldContextMenu.hectareLimit")}
                          </Typography>
                          <Typography variant="paragraph-m" className="progress-value">
                            {stats.enrolledFieldArea.toFixed(2)}/{currentFarm.hectareLimit} {t("fieldContextMenu.ha")}
                            {hectareLimitReached && <span className="progress-value"> ({t("fieldContextMenu.haLimitReached")})</span>}
                          </Typography>
                        </div>
                      )}
                      <LinkMoreInfo onClick={() => setShowHectareLimitInfoDrawer(true)}>{t("fieldContextMenu.learnMore")}</LinkMoreInfo>
                    </div>

                    <Tooltip title={enrollFieldsDisabledReason}>
                      <Button type="primary" block disabled={isEnrollmentLockedBySubmission} onClick={onEnrollFields}>
                        {hectareLimitReached ? t("fieldContextMenu.manageFieldEnrollment") : t("fieldContextMenu.enrollFields")}
                      </Button>
                    </Tooltip>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="action-buttons">
                  <Button type="secondary" block onClick={onAddFields}>
                    {t("fieldContextMenu.addFields")}
                  </Button>
                  <Button type="secondary" block onClick={onDeleteFields} className="delete-fields-button">
                    {t("fieldContextMenu.deleteFields")}
                  </Button>
                </div>
              </Card>
            </div>
          </>,
          document.body
        )}
      <ExtraInfo type="hectare-limit" visible={showHectareLimitInfoDrawer} setVisible={setShowHectareLimitInfoDrawer} />
    </div>
  );
};
